// Global stores and data registered on init
document.addEventListener('alpine:init', () => {
  // Get initial region data from the server-injected variable
  const initialRegion = window.tmeicRegionData
    ? window.tmeicRegionData.currentRegion
    : 'global';
  const initialRegionName = window.tmeicRegionData
    ? window.tmeicRegionData.currentRegionName
    : 'Global';

  // Region store
  Alpine.store('region', {
    // Initialize with server-provided values, fall back to persisted values if not available
    currentRegion: Alpine.$persist(initialRegion).as('currentRegion'),
    currentRegionName:
      Alpine.$persist(initialRegionName).as('currentRegionName'),
    searchState: {
      activeTab: 'products',
    },

    init() {
      // Log the initial region state
      console.log(
        `Region initialized: ${this.currentRegion} (${this.currentRegionName})`
      );
      this.setupEventListeners();
    },

    setupEventListeners() {
      const self = this;

      // Before swap - save current search tab state
      document.body.addEventListener('htmx:beforeSwap', function (event) {
        if (document.querySelector('.t-search-results')) {
          self._currentSearchTab = self.searchState.activeTab;
        }
      });

      // After swap - restore search tab state and reinitialize Alpine
      document.body.addEventListener('htmx:afterSwap', function (event) {
        if (document.querySelector('.t-search-results')) {
          if (self._currentSearchTab) {
            self.searchState.activeTab = self._currentSearchTab;
          }
        }
      });
    },

    // Method to change the active search tab
    setSearchTab(tabName) {
      if (this.searchState.activeTab !== tabName) {
        this.searchState.activeTab = tabName;
      }
    },

    changeRegion(region) {
      console.log(`Changing region to ${region}`);

      if (region == 'global') {
        this.currentRegion = 'global';
        this.currentRegionName = 'Global';
      } else {
        this.currentRegion = region;
        this.currentRegionName = region;
      }

      this.updateContext();
    },

    async updateContext() {
      console.log('Updating region context on server...');

      // Update the cookie first for backward compatibility
      document.cookie = `currentRegion=${this.currentRegion}; path=/; max-age=${
        30 * 24 * 60 * 60
      }`;

      // Then notify the server about the region change
      let args = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          action: 'change_region',
          region: this.currentRegion,
        }),
      };

      try {
        console.log('Sending AJAX request to update region...');
        let response = await fetch('/wp-admin/admin-ajax.php', args);

        if (response.ok) {
          let data = await response.json();
          if (data.region) {
            console.log(`Server region changed to ${data.region}`);

            // Force a small delay to ensure the cookie and session are set
            await new Promise((resolve) => setTimeout(resolve, 100));

            // Check if we need to redirect based on the current route
            this.checkCurrentRoute(data.region);
          }
        } else {
          console.error('Server returned an error when updating region');
          this.checkCurrentRoute(this.currentRegion);
        }
      } catch (error) {
        console.error('Error updating region on server:', error);
        // Even if the AJAX call fails, we should still try to redirect
        this.checkCurrentRoute(this.currentRegion);
      }
    },

    checkCurrentRoute(serverRegion) {
      let currentRoute = window.location.pathname + window.location.search;

      console.log(`Current route: ${currentRoute}`);

      // Skip HTMX swap for sitemap page - just reload it instead
      if (currentRoute.includes('/sitemap')) {
        console.log(
          'On sitemap page, skipping HTMX swap and letting page handle its own reload'
        );
        return;
      }

      // Extract language and region from URL
      const pathSegments = currentRoute.split('/').filter(Boolean);
      let language = null;
      let region = null;

      // Check if first segment is a language code
      const validLanguages = ['zh-hans', 'es'];
      if (pathSegments.length > 0 && validLanguages.includes(pathSegments[0])) {
        language = pathSegments[0];
        region = pathSegments[1] || null;
      } else {
        // No language prefix, so first segment might be region
        region = pathSegments[0] || null;
      }

      console.log(`Detected language: ${language}`);
      console.log(`Detected region: ${region}`);

      // Get the English region slug using WPML data
      const englishRegion = this.getEnglishRegionSlug(region, language);
      console.log(`English region slug: ${englishRegion}`);

      // Valid regions from the Region CPT (passed via PHP)
      const validEnglishRegions = window.tmeicRegionData?.validRegions || [];

      // Check if we're on a homepage or region page
      const isHomePage = currentRoute === '/';
      const isLanguageHomePage = language && pathSegments.length === 1; // e.g., /es/
      const isRegionPage = englishRegion && validEnglishRegions.includes(englishRegion) && 
                          (pathSegments.length === 1 || (language && pathSegments.length === 2));

      const shouldRedirect = isHomePage || isLanguageHomePage || isRegionPage;

      console.log(`Should redirect: ${shouldRedirect}`);

      if (shouldRedirect) {
        console.log('Redirecting to appropriate region...');
        this.redirectToRegion(currentRoute, serverRegion, englishRegion, language);
      } else {
        // For non-home pages, DO NOT redirect - just refresh the content
        console.log('Not on home page, skipping redirect');
        this.customHTMXSwap();
      }
    },

    getEnglishRegionSlug(regionSlug, language) {
      if (!regionSlug) return null;
      
      // Option 1: Use WPML data from the page
      // WPML often adds data attributes or meta tags with translation info
      const wpmlData = document.querySelector('[data-wpml-original-slug]');
      if (wpmlData) {
        return wpmlData.dataset.wpmlOriginalSlug;
      }

      // Option 2: Check for WPML JavaScript object
      if (window.wpml && window.wpml.getOriginalSlug) {
        return window.wpml.getOriginalSlug(regionSlug, language);
      }

      // Option 3: Look for meta tags or other WPML indicators
      const metaOriginal = document.querySelector('meta[name="wpml-original-slug"]');
      if (metaOriginal) {
        return metaOriginal.content;
      }

      // Option 4: Extract from alternate language links
      const englishLink = document.querySelector('link[hreflang="en"]');
      if (englishLink) {
        const englishPath = new URL(englishLink.href).pathname;
        const englishSegments = englishPath.split('/').filter(Boolean);
        return englishSegments[0]; // Assuming region is first segment in English
      }

      // Fallback: return as-is
      return regionSlug;
    },

    redirectToRegion(currentRoute, serverRegion, currentRegionInUrl, language) {
      // If we're already on the correct region page, no need to redirect
      if (currentRegionInUrl === serverRegion) {
        console.log('Already on the correct region page');
        return;
      }

      // Determine the new URL based on the region
      let newUrl;

      if (serverRegion === 'global') {
        // If switching to global, go to the main home page
        newUrl = language ? `/${language}/` : '/';
      } else {
        // Otherwise go to the regional home page
        newUrl = language ? `/${language}/${serverRegion}/` : `/${serverRegion}/`;
      }

      console.log(`Redirecting to ${newUrl} for region ${serverRegion}`);

      // Force a reload to the new URL
      console.log('Performing navigation to new URL...');
      window.location.replace(newUrl);
    },

    customHTMXSwap() {
      document.body.setAttribute('hx-get', window.location.href);
      document.body.setAttribute('hx-trigger', 'region-changed');
      document.body.setAttribute('hx-swap', 'none');

      window.requestAnimationFrame(() => {
        htmx.process(document.body);
        htmx.trigger(document.body, 'region-changed');
      });

      document.body.addEventListener(
        'htmx:afterSwap',
        () => {
          document.body.removeAttribute('hx-get');
          document.body.removeAttribute('hx-trigger');
          document.body.removeAttribute('hx-swap');
        },
        { once: true } // Prevent multiple triggers
      );
    },
  });

  // Desktop dropdown menu data
  Alpine.data('dropdown', () => ({
    activeMenu: '',
    isOpen: false,
    isSimpleDropdown: false,
    dropdownPosition: null,
    isTransitioning: false, // Add a flag to track transitions

    // Computed property for dropdown styles
    get dropdownStyles() {
      if (this.dropdownPosition) {
        return { left: `${this.dropdownPosition}px` };
      }
      return {};
    },

    init() {
      this.$nextTick(() => {
        document.addEventListener('click', (e) => {
          if (!this.isOpen) return;
          if (!this.$el.contains(e.target)) {
            this.closeDropdown();
          }
        });

        document.addEventListener('keydown', (e) => {
          if (e.key === 'Escape' && this.isOpen) {
            this.closeDropdown();
          }
        });
      });
    },

    ensureContentVisibility(id) {
      // This method ensures all content elements have appropriate visibility
      const allContents = document.querySelectorAll('.dropdown-content');

      allContents.forEach((content) => {
        const contentId = content.getAttribute('data-id');

        if (contentId === id) {
          // This is the active content - make sure it's visible
          content.style.opacity = '1';
          content.style.visibility = 'visible';
          content.style.transform = 'translateY(0)';
        } else {
          // All other content should be hidden
          content.style.opacity = '0';
          content.style.visibility = 'hidden';
          content.style.transform = 'translateY(5px)';
        }
      });
    },

    closeDropdown() {
      if (this.isTransitioning) return; // Prevent double-closing
      this.isTransitioning = true;

      // First set the open state to false (which will hide the dropdown via x-show)
      this.isOpen = false;

      // Reset content visibility on close
      const allContents = document.querySelectorAll('.dropdown-content');
      allContents.forEach((content) => {
        content.style.opacity = '';
        content.style.visibility = '';
        content.style.transform = '';
      });

      // Re-enable scrolling
      document.body.style.overflow = '';

      // Wait for the dropdown to be fully hidden before resetting other properties
      setTimeout(() => {
        this.isSimpleDropdown = false;
        this.dropdownPosition = null;
        this.isTransitioning = false;
      }, 300); // Should be longer than dropdown transition time
    },

    updateDropdown(id, isSimple = false) {
      // Prevent actions during transitions
      if (this.isTransitioning) return;

      // If already open and clicking the same menu item, close it
      if (this.isOpen && this.activeMenu === id) {
        this.closeDropdown();
        return;
      }

      // If already open but clicking different menu item
      if (this.isOpen && this.activeMenu !== id) {
        this.isTransitioning = true;

        // Hide current content immediately
        const current = document.querySelector(
          `.dropdown-content[data-id="${this.activeMenu}"]`
        );

        if (current) {
          current.style.opacity = '0';
          current.style.visibility = 'hidden';
          current.style.transform = 'translateY(5px)';
        }

        // Slightly longer delay to ensure the transition completes
        setTimeout(() => {
          this.activeMenu = id;
          this.isSimpleDropdown = isSimple;

          // Apply position for simple dropdowns
          if (isSimple) {
            this.calculatePosition(id);
          } else {
            this.dropdownPosition = null;
          }

          // Ensure the new content is visible
          this.ensureContentVisibility(id);

          // End transition state
          this.isTransitioning = false;
        }, 250);

        return;
      }

      // If closed, open it with the selected menu
      this.activeMenu = id;
      this.isOpen = true;
      this.isSimpleDropdown = isSimple;

      // Calculate position for simple dropdowns
      if (isSimple) {
        this.$nextTick(() => {
          this.calculatePosition(id);

          // Double-check content visibility after a short delay
          setTimeout(() => {
            this.ensureContentVisibility(id);
          }, 50);
        });
      } else {
        this.dropdownPosition = null;
      }

      // Ensure content is visible
      this.$nextTick(() => {
        this.ensureContentVisibility(id);
      });

      // Disable scrolling when dropdown is open
      document.body.style.overflow = 'hidden';
    },

    calculatePosition(id) {
      const button = document.querySelector(`button[data-id="${id}"]`);

      if (button) {
        const rect = button.getBoundingClientRect();
        this.dropdownPosition = rect.left;
      }
    },
  }));

  // Mobile menu store
  Alpine.store('mobileMenu', {
    mobileMenuOpen: false,

    toggle() {
      this.mobileMenuOpen = !this.mobileMenuOpen;
    },
  });

  // Global theme store, if needed
  Alpine.store('theme', {
    darkMode: false,

    toggle() {
      this.darkMode = !this.darkMode;
    },
  });

  // Subnav store to track intersections
  Alpine.store('subnav', {
    activeMenu: 'info',
    activeMenuName: '',
    mobileOpen: false,
    translations: {},

    init() {
      // Get translations from data attribute
      const dropdown = document.querySelector('.c-subnav__dropdown');
      if (dropdown) {
        this.translations = JSON.parse(dropdown.dataset.translations || '{}');
        this.activeMenuName = this.translations.info || 'Product Info';
      }
    },

    updateMenu(name) {
      this.activeMenu = name;
      this.activeMenuName = this.translations[name] || 'Product Info';

      if (this.mobileOpen) {
        this.mobileOpen = false;
      }
    },

    toggleMobile() {
      this.mobileOpen = !this.mobileOpen;
    },
  });

  // Subnav data to contorl scroll functionality
  Alpine.data('subnav', () => ({
    scrolled: false,
    offset: 0,
    navHeight: 0,
    lastScrollPosition: 0,
    ticking: false,
    // mobileOpen: false,

    init() {
      this.navHeight = document.querySelector('.c-header').offsetHeight;
      this.offset =
        this.$el.offsetTop + this.$el.offsetHeight + this.navHeight + 64;
    },

    handleScroll() {
      this.lastScrollPosition = window.scrollY;

      if (!this.ticking) {
        window.requestAnimationFrame(() => {
          if (this.lastScrollPosition > this.offset) {
            this.scrolled = true;
          } else {
            this.scrolled = false;
          }
          this.ticking = false;
        });
        this.ticking = true;
      }
    },

    toggleMobile() {
      this.mobileOpen = !this.mobileOpen;
    },
  }));

  // Tabs Block component
  Alpine.data('tabsBlock', (startingTab, tabItems) => ({
    activeTab: startingTab,
    mobileDropdownOpen: false,
    tabTitles: {},

    init() {
      // Build the titles lookup object
      this.tabTitles = tabItems.reduce((acc, tab) => {
        acc[tab.slug] = tab.title;
        return acc;
      }, {});

      this.$nextTick(() => {
        // Check URL for tab parameter
        const urlParams = new URLSearchParams(window.location.search);
        const tabParam = urlParams.get('tab');
        if (tabParam) {
          this.activeTab = tabParam;
        }
      });
    },

    setActiveTab(tabId) {
      this.activeTab = tabId;
      // Update URL without page refresh
      const url = new URL(window.location);
      url.searchParams.set('tab', tabId);
      window.history.pushState({}, '', url);
    },

    getActiveTabTitle() {
      return this.tabTitles[this.activeTab] || 'Select Option';
    },
  }));
});
